import type { Configuration } from '@azure/msal-browser'
import { PublicClientApplication } from '@azure/msal-browser'

export interface AuthConfig {
  enabled: boolean
  tenantId: string | null
  clientId: string | null
  redirectUri: string
  // E-Mail-/OTP-Login zugelassen (Default true). Anonym vor der Auth geladen,
  // damit der Login-Screen die OTP-Option vor der Anmeldung kennt.
  emailOtpEnabled?: boolean
}

export const apiScopes = [import.meta.env.VITE_API_SCOPE]
export const loginRequest = { scopes: ['User.Read', ...apiScopes] }

export let pca!: PublicClientApplication // set by initMsal() before any consumer runs
export let authEnabled = false // set by initMsal()
// E-Mail-/OTP-Login zugelassen (Default true). set by initMsal() — der Login-Screen
// blendet die OTP-Option aus, wenn dies false ist.
export let otpEnabled = true

function buildConfig(c: AuthConfig): Configuration {
  return {
    auth: {
      clientId: c.clientId ?? '',
      authority: `https://login.microsoftonline.com/${c.tenantId ?? 'common'}`,
      redirectUri: c.redirectUri || `${window.location.origin}/auth/callback`,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: { cacheLocation: 'localStorage' },
  }
}

async function fetchAuthConfig(): Promise<AuthConfig> {
  try {
    const res = await fetch('/api/auth/config')
    if (res.ok) return (await res.json()) as AuthConfig
  } catch {
    /* fall through to dev fallback */
  }
  // Dev fallback: build-time VITE_ENTRA_* (so local dev without the API still works)
  const tenantId = (import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined) ?? null
  const clientId = (import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined) ?? null
  return {
    enabled: !!tenantId && !!clientId,
    tenantId,
    clientId,
    redirectUri: `${window.location.origin}/auth/callback`,
  }
}

export async function initMsal(): Promise<void> {
  const cfg = await fetchAuthConfig()
  authEnabled = cfg.enabled
  // Default true: fehlt das Feld (alte API/Dev-Fallback), bleibt OTP angeboten.
  otpEnabled = cfg.emailOtpEnabled !== false
  pca = new PublicClientApplication(buildConfig(cfg))
  await pca.initialize()
  const accounts = pca.getAllAccounts()
  if (accounts[0]) pca.setActiveAccount(accounts[0])
}
